import React, { useState, type FC, type MouseEventHandler } from 'react';

import axios, { type AxiosResponse } from 'axios';

import { Button } from '@shared/components/bootstrap';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { useAxiosRequest } from '@admin/hooks';

type Affiliate = {
  id: number;
  name: string;
};

const useAffiliateDeleteRequest = ({ variables: { id } }: { variables: { id: number } }) => {
  const [request, setRequest] = useState<Promise<AxiosResponse> | undefined>(undefined);
  const { loading, response, error } = useAxiosRequest(request);

  const execute = (): Promise<AxiosResponse> => {
    const request = axios.delete(`/marketing/affiliates/${id}`);
    setRequest(request);
    return request;
  };

  return [execute, { loading, response, error }] as const;
};

export const AffiliateDeleteButton: FC<{
  affiliate: Affiliate;
  onDelete(): void;
}> = ({ affiliate, onDelete }) => {
  const [execute, { loading }] = useAffiliateDeleteRequest({ variables: { id: affiliate.id } });
  const { confirm, confirmation } = useConfirmation();

  const onClick: MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const confirmed = await confirm({
      title: 'Delete Affiliate?',
      description: `Are you sure you want to delete ${affiliate.name}?`,
    });

    if (confirmed !== ConfirmationResponse.Confirm) return;

    try {
      await execute();
      onDelete();
    } catch (error) {
      alert(error);
    }
  };

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      <Button kind="danger" disabled={loading} onClick={onClick}>
        Delete
      </Button>
    </>
  );
};
